import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, TrendingUp, TrendingDown } from 'lucide-react';

const ScheduleBvsHTS = () => {
  const comparisonData = [
    {
      category: "Primary Use",
      scheduleb: "Export statistics and reporting",
      hts: "Import classification and duty determination"
    },
    {
      category: "Trade Direction",
      scheduleb: "Exports from the United States",
      hts: "Imports into the United States"
    },
    {
      category: "Governing Authority",
      scheduleb: "U.S. Census Bureau",
      hts: "U.S. International Trade Commission / CBP"
    },
    {
      category: "Code Length",
      scheduleb: "10 digits",
      hts: "10 digits"
    },
    {
      category: "Based On",
      scheduleb: "Harmonized System (international standard)",
      hts: "Harmonized System + U.S.-specific additions"
    },
    {
      category: "First 6 Digits",
      scheduleb: "Match international HS codes",
      hts: "Match international HS codes"
    },
    {
      category: "Last 4 Digits",
      scheduleb: "U.S. export statistical suffixes",
      hts: "U.S. import statistical suffixes"
    },
    {
      category: "Duty Rates",
      scheduleb: "Not applicable - no export duties",
      hts: "Shows applicable import duty rates"
    },
    {
      category: "Required For",
      scheduleb: "Electronic Export Information (EEI) filing",
      hts: "Customs entry and duty payment"
    },
    {
      category: "Updates",
      scheduleb: "Updated periodically by Census Bureau",
      hts: "Updated annually by USITC"
    },
    {
      category: "Legal Implications",
      scheduleb: "Misclassification affects trade statistics",
      hts: "Misclassification can result in penalties and duty adjustments"
    },
    {
      category: "Binding Rulings",
      scheduleb: "Not applicable",
      hts: "Available from CBP for duty purposes"
    }
  ];

  return (
    <div className="comparison-page">
      <SEO 
        title="Schedule B vs HTS: Export and Import Classification Codes"
        description="Understand Schedule B (export) and HTS (import) classification codes. Learn when to use each system and how they relate to international trade."
        keywords="Schedule B, HTS code, export classification, import classification, harmonized system, trade statistics"
        path="/schedule-b-vs-hts"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Resources', path: '/resources' },
        { label: 'Schedule B vs HTS', path: '/schedule-b-vs-hts' }
      ]} />
      
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Schedule B vs HTS Codes
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              Schedule B and HTS codes are both 10-digit classification systems based on the Harmonized System, but they serve different purposes in international trade. This guide explains when to use each system.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="h-8 w-8 text-indigo-600" />
                <h3 className="text-2xl font-bold text-indigo-900">Schedule B</h3>
              </div>
              <p className="text-indigo-800 mb-4 font-semibold">For U.S. Exports</p>
              <ul className="space-y-2 text-indigo-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span>Used in <strong>EEI filings</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span>Maintained by <strong>Census Bureau</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span><strong>No duty rates</strong> (U.S. doesn't charge export duties)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <span>Statistical reporting purpose</span>
                </li>
              </ul>
            </div>
            <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <TrendingDown className="h-8 w-8 text-teal-600" />
                <h3 className="text-2xl font-bold text-teal-900">HTS</h3>
              </div>
              <p className="text-teal-800 mb-4 font-semibold">For U.S. Imports</p>
              <ul className="space-y-2 text-teal-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Determines <strong>import duties</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Maintained by <strong>USITC</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span><strong>Duty rates</strong> vary by classification</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span>Required for customs clearance</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Side-by-Side Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="text-left p-4 font-bold text-lg">Feature</th>
                  <th className="text-left p-4 font-bold text-lg">Schedule B</th>
                  <th className="text-left p-4 font-bold text-lg">HTS</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-900 border-b border-gray-200">
                      {row.category}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.scheduleb}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.hts}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Important Concepts
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                The First 6 Digits Are the Same
              </h3>
              <p className="text-gray-700 mb-4">
                Both Schedule B and HTS follow the international Harmonized System (HS) for the first 6 digits. This means:
              </p>
              <div className="bg-slate-50 p-6 rounded-lg">
                <p className="font-mono text-lg mb-2">Example: Laptop Computer</p>
                <p className="font-mono"><span className="text-indigo-600 font-bold">8471.30</span><span className="text-gray-400">.XXXX</span> ← First 6 digits (International HS)</p>
                <p className="text-sm text-gray-600 mt-2">The last 4 digits differ between Schedule B and HTS</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Can I Use the Same Code for Both?
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>Sometimes, but not always.</strong> While the first 6 digits match, the last 4 statistical suffixes often differ. Key points:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-slate-600 font-bold">•</span>
                  <span>Schedule B focuses on export statistics</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-600 font-bold">•</span>
                  <span>HTS includes duty implications</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-600 font-bold">•</span>
                  <span>Each system has unique statistical breakouts</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-600 font-bold">•</span>
                  <span>Maintain both in your product database</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-8">
              <h3 className="text-xl font-bold text-amber-900 mb-3">
                Best Practice
              </h3>
              <p className="text-amber-800">
                If you both import and export products, classify them under both systems. This ensures you have the correct codes for EEI filings (Schedule B) and customs entries (HTS), avoiding delays and penalties.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Classification Services?
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Our classification specialists can determine both Schedule B and HTS codes for your products, ensuring accurate reporting and duty calculations.
          </p>
          <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
              Schedule Classification Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ScheduleBvsHTS;